/**
 * AuthProvider — Supabase Auth (email/password) session context.
 *
 * v1 owns auth via Supabase (the CLAUDE.md Urable-piggyback pattern is
 * intentionally dropped) so every user gets a real Supabase JWT and therefore
 * real per-user Postgres RLS. The JWT is what the server verifies (against
 * SUPABASE_JWKS_URL) to derive {userId, role, account_ref} from `profiles`.
 *
 * This provider exposes:
 *   - session / user (from Supabase),
 *   - the user's profile {role, account_ref, ...} loaded from `profiles`,
 *   - signIn / signOut helpers,
 *   - loading + error state for friendly UX.
 *
 * The role/account_ref shown here are for UI gating only (showing/hiding the
 * manager tab). Authorization is ALWAYS re-derived and enforced server-side and
 * by RLS — never trust the client copy.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Session, User } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { AppRole, Profile } from "@/types";

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  role: AppRole | null;
  accountRef: string | null;
  /** Admin-managed permission to open the live-monitor firehose. */
  canViewLiveMonitor: boolean;
  /** True until the initial session + profile load settles. */
  initializing: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [initializing, setInitializing] = useState(true);
  const queryClient = useQueryClient();

  // Load the profile row for the signed-in user (role + account_ref).
  async function loadProfile(userId: string): Promise<void> {
    const { data, error } = await supabase
      .from("profiles")
      .select("id,email,full_name,role,active,account_ref,can_view_live_monitor")
      .eq("id", userId)
      .single();
    if (error) {
      // RLS or missing row — treat as no profile; UI will route to login/error.
      setProfile(null);
      return;
    }
    setProfile(data as Profile);
  }

  useEffect(() => {
    let mounted = true;

    // 1. Hydrate any persisted session (chrome.storage.local / localStorage).
    supabase.auth.getSession().then(async ({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      if (data.session?.user) {
        await loadProfile(data.session.user.id);
      }
      if (mounted) setInitializing(false);
    });

    // 2. Subscribe to future auth changes (refresh, sign-out from another tab).
    const { data: sub } = supabase.auth.onAuthStateChange(async (_event, next) => {
      if (!mounted) return;
      setSession(next);
      if (next?.user) {
        await loadProfile(next.user.id);
      } else {
        setProfile(null);
      }
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  async function signIn(email: string, password: string): Promise<void> {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error; // LoginScreen maps to a friendly message.
  }

  async function signOut(): Promise<void> {
    await supabase.auth.signOut();
    setProfile(null);
    // Drop every cached query result (calls, transcripts, self-scores incl.
    // rep notes, …) so nothing from this identity survives into the next
    // sign-in on a shared computer. Query keys carry no user component, so
    // clearing is the only safe boundary.
    queryClient.clear();
  }

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      profile,
      role: profile?.role ?? null,
      accountRef: profile?.account_ref ?? null,
      canViewLiveMonitor: profile?.can_view_live_monitor ?? false,
      initializing,
      signIn,
      signOut,
    }),
    [session, profile, initializing],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
